import type React from 'react';
import {type PropsWithChildren} from 'react';
import {useLocalization} from 'localization';
import {isMockWorkId, useGetWorkQuery} from 'services/metadata';
import {useGetNotesByContextIdsQuery} from 'services/notes';
import {useMockContext} from 'services/utils/contexts';
import {SceneLoader} from 'components/scene/SceneLoader';

type Props = {
  workId?: string;
};

/**
 * Load data by work instead of by each manifestation.
 */
export const WorkPreviewDataLoader: React.FC<PropsWithChildren<Props>> = ({
  workId,
  children,
}) => {
  const {t} = useLocalization();
  const mock = useMockContext() || (workId && isMockWorkId(workId));

  const {data: workMetadataData, isLoading: isWorkLoading} = useGetWorkQuery(
    {id: workId!},
    {
      skip: mock || !workId,
    },
  );

  const manifestationIds =
    ((workMetadataData?.manifestations ?? [])
      .map(m => m.id)
      .filter(e => e) as string[]) ?? [];

  const {isLoading: isNotesLoading} = useGetNotesByContextIdsQuery(
    manifestationIds,
    {
      skip: manifestationIds.length === 0,
    },
  );

  if (isWorkLoading || isNotesLoading) {
    return (
      <SceneLoader loadingType={t('general.entity.notes')} applySceneHeight />
    );
  }

  return children;
};
